import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, AlertTriangle, Leaf, Upload as UploadIcon, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Client-side image optimization: resize to ≤1600px longest side and re-encode
// as JPEG at q=0.82. Keeps page loads fast when the image is embedded as a
// data-URL directly on the product record.
const optimizeImage = (file, maxDim = 1600, quality = 0.82) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve({ dataUrl: canvas.toDataURL('image/jpeg', quality), width: w, height: h });
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'office-supplies',
    price: '',
    retail_price: '',
    stock: '',
    reorder_level: '20',
    image: '',
    is_green: false
  });

  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(res.data.map(c => ({ value: c.slug, label: c.name })));
    } catch (err) {
      // Fallback to default categories
      setCategories([
        { value: 'office-supplies', label: 'Office Supplies' },
        { value: 'paper', label: 'Paper' },
        { value: 'furniture', label: 'Furniture' },
        { value: 'technology-facilities', label: 'Technology Facilities' },
        { value: 'ink-toner', label: 'Ink & Toner' },
        { value: 'cleaning-products', label: 'Cleaning Products' }
      ]);
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch products",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/products`, {
        ...formData,
        price: parseFloat(formData.price),
        retail_price: formData.retail_price === '' ? null : parseFloat(formData.retail_price),
        stock: parseInt(formData.stock),
        reorder_level: parseInt(formData.reorder_level) || 20
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: "Product created successfully",
      });
      
      setIsCreateDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create product",
        variant: "destructive"
      });
    }
  };

  const handleUpdateProduct = async () => {
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        ...formData,
        price: parseFloat(formData.price),
        retail_price: formData.retail_price === '' ? null : parseFloat(formData.retail_price),
        stock: parseInt(formData.stock),
        reorder_level: parseInt(formData.reorder_level) || 20
      };
      
      await axios.put(`${API}/products/${selectedProduct.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: "Product updated successfully",
      });
      
      setIsEditDialogOpen(false);
      setSelectedProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update product",
        variant: "destructive"
      });
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (!window.confirm(`Are you sure you want to delete ${productName}?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: "Product deleted successfully",
      });
      
      fetchProducts();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete product",
        variant: "destructive"
      });
    }
  };

  const openEditDialog = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      category: product.category,
      price: product.price.toString(),
      retail_price: product.retail_price != null ? product.retail_price.toString() : '',
      stock: product.stock.toString(),
      reorder_level: (product.reorder_level || 20).toString(),
      image: product.image,
      is_green: !!product.is_green
    });
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'office-supplies',
      price: '',
      retail_price: '',
      stock: '',
      reorder_level: '20',
      image: '',
      is_green: false
    });
  };

  const handleUploadExcel = async () => {
    if (!uploadFile) {
      toast({
        title: "Error",
        description: "Please select a file to upload",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await axios.post(`${API}/products/upload-excel`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({
        title: "Success",
        description: response.data.message,
      });

      setIsUploadDialogOpen(false);
      setUploadFile(null);
      fetchProducts();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to upload file",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClearAllProducts = async () => {
    setClearing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Success",
        description: response.data.message,
      });

      setIsClearDialogOpen(false);
      fetchProducts();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to clear products",
        variant: "destructive"
      });
    } finally {
      setClearing(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-4 sm:p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-4xl font-bold text-slate-800">Products Management</h1>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <Button 
            variant="outline"
            className="border-red-500 text-red-500 hover:bg-red-50 text-xs sm:text-sm"
            onClick={() => setIsClearDialogOpen(true)}
          >
            Clear All
          </Button>
          <Button 
            variant="outline"
            className="border-purple-600 text-purple-600 hover:bg-purple-50 text-xs sm:text-sm"
            onClick={() => setIsUploadDialogOpen(true)}
          >
            Upload Excel
          </Button>
          <Button 
            className="bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm"
            onClick={() => {
              resetForm();
              setIsCreateDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-1 sm:mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="mb-6 border-2">
        <CardContent className="p-4">
          <Input
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card className="border-2">
        <CardHeader>
          <CardTitle>All Products ({filteredProducts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3 text-slate-700">Image</th>
                  <th className="text-left p-3 text-slate-700">Name</th>
                  <th className="text-left p-3 text-slate-700">Category</th>
                  <th className="text-left p-3 text-slate-700">Price</th>
                  <th className="text-left p-3 text-slate-700">Stock</th>
                  <th className="text-left p-3 text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map(product => (
                  <tr key={product.id} className="border-t hover:bg-slate-50">
                    <td className="p-3">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-12 h-12 object-cover rounded"
                      />
                    </td>
                    <td className="p-3 font-medium text-slate-800">
                      <span className="inline-flex items-center gap-1.5">
                        {product.name}
                        {product.is_green && (
                          <span title="Green / eco-friendly" className="inline-flex items-center gap-1 text-[10px] font-semibold bg-green-100 text-green-800 px-1.5 py-0.5 rounded" data-testid={`green-badge-${product.id}`}>
                            <Leaf className="h-3 w-3" /> Green
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="p-3 text-slate-600 capitalize">
                      {product.category.replace('-', ' ')}
                    </td>
                    <td className="p-3 text-slate-800 font-semibold">${product.price}</td>
                    <td className="p-3">
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        product.stock > 50
                          ? 'bg-green-100 text-green-700'
                          : product.stock > 0
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {product.stock}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(product)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteProduct(product.id, product.name)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Product Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Product</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Product Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                  <SelectTrigger id="category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="price">Portal / Contract Price *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  required
                  data-testid="product-price"
                />
                <p className="text-[10px] text-slate-500 mt-1">Shown to portal (B2B) buyers.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="retail_price">Retail Price</Label>
                <Input
                  id="retail_price"
                  type="number"
                  step="0.01"
                  value={formData.retail_price}
                  onChange={(e) => setFormData({...formData, retail_price: e.target.value})}
                  placeholder="Public storefront price (optional)"
                  data-testid="product-retail-price"
                />
                <p className="text-[10px] text-slate-500 mt-1">Shown on the public storefront. Falls back to Portal Price if empty.</p>
              </div>
              <div></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="stock">Stock Quantity *</Label>
                <Input
                  id="stock"
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData({...formData, stock: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="reorder_level">Reorder Level</Label>
                <Input
                  id="reorder_level"
                  type="number"
                  value={formData.reorder_level}
                  onChange={(e) => setFormData({...formData, reorder_level: e.target.value})}
                  placeholder="Default: 20"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="image">Image *</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  id="image"
                  type="text"
                  value={formData.image}
                  onChange={(e) => setFormData({...formData, image: e.target.value})}
                  placeholder="Paste image URL or use Upload →"
                  required
                />
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="product-image-upload"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    try {
                      const { dataUrl, width, height } = await optimizeImage(f);
                      setFormData((prev) => ({ ...prev, image: dataUrl }));
                      toast({ title: 'Image optimized', description: `Web-optimized to ${width}×${height}` });
                    } catch (err) {
                      toast({ title: 'Image error', description: err?.message || 'Could not process image', variant: 'destructive' });
                    } finally { e.target.value = ''; }
                  }}
                />
                <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('image-upload')?.click()}>
                  <UploadIcon className="h-4 w-4 mr-1" /> Upload
                </Button>
              </div>
              {formData.image && (
                <img src={formData.image} alt="preview" className="mt-2 h-20 w-20 object-cover rounded border" />
              )}
              <p className="text-[11px] text-slate-500 mt-1">Uploads auto-resize to a Web-Optimized JPEG (≤1600px, ~q82).</p>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <input
                id="is_green"
                type="checkbox"
                checked={!!formData.is_green}
                onChange={(e) => setFormData({...formData, is_green: e.target.checked})}
                className="h-4 w-4 accent-green-600"
                data-testid="product-is-green"
              />
              <Label htmlFor="is_green" className="flex items-center gap-1 text-sm font-normal cursor-pointer">
                <Leaf className="h-4 w-4 text-green-600" />
                Green / eco-friendly product
              </Label>
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateProduct} className="bg-purple-600 hover:bg-purple-700 text-white">
                Create Product
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Product</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Product Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-description">Description *</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-category">Category *</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                  <SelectTrigger id="edit-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-price">Portal / Contract Price *</Label>
                <Input
                  id="edit-price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  required
                  data-testid="edit-product-price"
                />
                <p className="text-[10px] text-slate-500 mt-1">Shown to portal (B2B) buyers.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-retail_price">Retail Price</Label>
                <Input
                  id="edit-retail_price"
                  type="number"
                  step="0.01"
                  value={formData.retail_price}
                  onChange={(e) => setFormData({...formData, retail_price: e.target.value})}
                  placeholder="Public storefront price (optional)"
                  data-testid="edit-product-retail-price"
                />
                <p className="text-[10px] text-slate-500 mt-1">Shown on the public storefront. Falls back to Portal Price if empty.</p>
              </div>
              <div></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-stock">Stock Quantity *</Label>
                <Input
                  id="edit-stock"
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData({...formData, stock: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-reorder_level">Reorder Level</Label>
                <Input
                  id="edit-reorder_level"
                  type="number"
                  value={formData.reorder_level}
                  onChange={(e) => setFormData({...formData, reorder_level: e.target.value})}
                  placeholder="Default: 20"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="edit-image">Image *</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  id="edit-image"
                  type="text"
                  value={formData.image}
                  onChange={(e) => setFormData({...formData, image: e.target.value})}
                  placeholder="Paste image URL or use Upload →"
                  required
                />
                <input
                  id="edit-image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="edit-product-image-upload"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    try {
                      const { dataUrl, width, height } = await optimizeImage(f);
                      setFormData((prev) => ({ ...prev, image: dataUrl }));
                      toast({ title: 'Image optimized', description: `Web-optimized to ${width}×${height}` });
                    } catch (err) {
                      toast({ title: 'Image error', description: err?.message || 'Could not process image', variant: 'destructive' });
                    } finally { e.target.value = ''; }
                  }}
                />
                <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('edit-image-upload')?.click()}>
                  <UploadIcon className="h-4 w-4 mr-1" /> Upload
                </Button>
              </div>
              {formData.image && (
                <img src={formData.image} alt="preview" className="mt-2 h-20 w-20 object-cover rounded border" />
              )}
              <p className="text-[11px] text-slate-500 mt-1">Uploads auto-resize to a Web-Optimized JPEG (≤1600px, ~q82).</p>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <input
                id="edit-is_green"
                type="checkbox"
                checked={!!formData.is_green}
                onChange={(e) => setFormData({...formData, is_green: e.target.checked})}
                className="h-4 w-4 accent-green-600"
                data-testid="edit-product-is-green"
              />
              <Label htmlFor="edit-is_green" className="flex items-center gap-1 text-sm font-normal cursor-pointer">
                <Leaf className="h-4 w-4 text-green-600" />
                Green / eco-friendly product
              </Label>
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateProduct} className="bg-purple-600 hover:bg-purple-700 text-white">
                Update Product
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Excel Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Products from Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Instructions:</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Download the Excel template below</li>
                <li>Fill in your product data (name, description, category, price, stock, image URL)</li>
                <li>Upload the completed file</li>
              </ol>
              <div className="mt-3">
                <a 
                  href="/products_template.xlsx" 
                  download
                  className="text-sm text-blue-600 hover:text-blue-700 underline font-medium"
                >
                  📥 Download Excel Template
                </a>
              </div>
            </div>

            <div>
              <Label htmlFor="excel-file">Select Excel File *</Label>
              <Input
                id="excel-file"
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="cursor-pointer"
              />
              {uploadFile && (
                <p className="text-sm text-slate-600 mt-2">
                  Selected: {uploadFile.name}
                </p>
              )}
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-600 font-medium mb-1">Valid Categories:</p>
              <p className="text-xs text-slate-500">
                office-supplies, paper, furniture, technology, ink-toner, groceries, cleaning
              </p>
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={() => {
                setIsUploadDialogOpen(false);
                setUploadFile(null);
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleUploadExcel} 
                className="bg-purple-600 hover:bg-purple-700 text-white"
                disabled={uploading || !uploadFile}
              >
                {uploading ? 'Uploading...' : 'Upload Products'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Clear All Products Confirmation Dialog */}
      <Dialog open={isClearDialogOpen} onOpenChange={setIsClearDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Clear All Products
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium">Warning!</p>
              <p className="text-sm text-red-700 mt-1">
                This action will permanently delete ALL {products.length} products from the database. 
                This cannot be undone.
              </p>
            </div>
            <p className="text-slate-600 text-sm">
              Are you sure you want to proceed? You can upload new products via Excel after clearing.
            </p>
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={() => setIsClearDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleClearAllProducts} 
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={clearing}
              >
                {clearing ? 'Clearing...' : 'Yes, Clear All Products'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
